import React from "react";
import { Link } from "react-router-dom";

export const formatNumber = (n) => new Intl.NumberFormat("en-US").format(n);

export const formatDuration = (seconds) => {
    const s = Math.max(0, Math.round(seconds || 0));
    if (s < 60) return `${s}s`;
    if (s < 3600) {
        const m = Math.floor(s / 60);
        const sr = s % 60;
        return sr ? `${m}m ${sr}s` : `${m}m`;
    }
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
};

// Display companion for formatDuration. Renders digits at full parent size
// with unit letters at ~50% size, so big-number hero treatments don't get
// dominated by long strings like "1h 43m". Use inside a heading-sized span.
export const Duration = ({ seconds }) => {
    const s = Math.max(0, Math.round(seconds || 0));
    let parts;
    if (s < 60) {
        parts = [[s, "s"]];
    } else if (s < 3600) {
        const m = Math.floor(s / 60);
        const sr = s % 60;
        parts = sr ? [[m, "m"], [sr, "s"]] : [[m, "m"]];
    } else {
        const h = Math.floor(s / 3600);
        const m = Math.round((s % 3600) / 60);
        parts = m ? [[h, "h"], [m, "m"]] : [[h, "h"]];
    }
    return (
        <>
            {parts.map(([digits, unit], i) => (
                <React.Fragment key={i}>
                    {i > 0 && <span className="inline-block w-[0.18em]" />}
                    {digits}
                    <span className="text-[0.45em] font-medium text-brand-muted ml-[0.06em] tracking-normal align-baseline">
                        {unit}
                    </span>
                </React.Fragment>
            ))}
        </>
    );
};

export const PLATFORM_LABELS = {
    facebook: "Facebook",
    x: "X",
    threads: "Threads",
    whatsapp: "WhatsApp",
    linkedin: "LinkedIn",
    instagram: "Instagram",
    tiktok: "TikTok",
};

export const PLATFORM_COLORS = {
    facebook: "bg-[#3B5998]",
    x: "bg-brand-ink",
    threads: "bg-black",
    whatsapp: "bg-[#5DCA45]",
    linkedin: "bg-[#2D64BC]",
    instagram: "bg-[#E48242]",
    tiktok: "bg-zinc-700",
    "(direct)": "bg-brand-faint",
};

export const platformLabel = (k) => (k === "(direct)" ? "(direct)" : PLATFORM_LABELS[k] || k);
export const platformColor = (k) => PLATFORM_COLORS[k] || "bg-brand-faint";

export const initial = (name) =>
    (name && name.length > 0 ? name.charAt(0).toUpperCase() : "?");

export const countryFlag = (code) => {
    if (!code || typeof code !== "string" || code.length !== 2) return "";
    const c = code.toUpperCase();
    const a = c.charCodeAt(0);
    const b = c.charCodeAt(1);
    if (a < 65 || a > 90 || b < 65 || b > 90) return "";
    return String.fromCodePoint(0x1f1e6 + (a - 65)) + String.fromCodePoint(0x1f1e6 + (b - 65));
};

export const asRows = (v) => (Array.isArray(v) ? v : []);

// From a list of {source, medium, content, term, campaign, y} combos, derive a
// sorted leaderboard. Optional `campaignSlug` scopes the leaderboard to one
// track; without it, every share-attributed combo is counted.
export const buildMemberRows = (comboRows, termRows, campaignSlug = null, byTerm = null) => {
    const filtered = comboRows.filter((c) => {
        if (c.source !== "ambasada-share") return false;
        if (campaignSlug && c.campaign !== campaignSlug) return false;
        return true;
    });
    const breakdownByTerm = new Map();
    const totalByTerm = new Map();
    filtered.forEach((c) => {
        const term = c.term || "";
        const medium = c.medium || "(direct)";
        if (!breakdownByTerm.has(term)) breakdownByTerm.set(term, {});
        const map = breakdownByTerm.get(term);
        map[medium] = (map[medium] || 0) + (c.y || 0);
        totalByTerm.set(term, (totalByTerm.get(term) || 0) + (c.y || 0));
    });
    const nameById = new Map();
    termRows.forEach((r) => {
        if (r.name && typeof r.name === "string" && r.name.length > 0) {
            nameById.set(r.x, r.name);
        }
    });
    const statsFor = (term) => {
        if (!byTerm) return { totaltime: null, visits: null };
        const s = byTerm[term] && byTerm[term].stats;
        return {
            totaltime: (s && s.totaltime) || 0,
            visits: (s && s.visits) || 0,
        };
    };
    const named = [];
    let anonTotal = totalByTerm.get("") || 0;
    const anonBreakdown = breakdownByTerm.get("") || {};
    totalByTerm.forEach((total, term) => {
        if (!term) return;
        const breakdown = breakdownByTerm.get(term) || {};
        const name = nameById.get(term);
        if (name) {
            named.push({ id: term, name, total, breakdown, ...statsFor(term) });
        } else {
            anonTotal += total;
            Object.entries(breakdown).forEach(([k, v]) => {
                anonBreakdown[k] = (anonBreakdown[k] || 0) + v;
            });
        }
    });
    if (byTerm) {
        named.sort((a, b) => (b.totaltime - a.totaltime) || (b.total - a.total));
    } else {
        named.sort((a, b) => b.total - a.total);
    }
    const list = [...named];
    if (anonTotal > 0) {
        list.push({ id: "__anon__", name: "Anonymous shares", total: anonTotal, breakdown: anonBreakdown, isAnon: true, totaltime: 0, visits: 0 });
    }
    return list;
};

export const buildPlatformList = (shareCombos, total) => {
    const map = new Map();
    shareCombos.forEach((c) => {
        const k = c.medium || "(direct)";
        map.set(k, (map.get(k) || 0) + (c.y || 0));
    });
    return Array.from(map.entries())
        .map(([k, v]) => ({
            key: k,
            label: platformLabel(k),
            value: v,
            sub: total > 0 ? `${Math.round((v / total) * 100)}%` : null,
        }))
        .sort((a, b) => b.value - a.value);
};

export const Section = ({ eyebrow, title, lede, children }) => (
    <section className="mt-16 sm:mt-20">
        <div className="mb-6 sm:mb-8">
            {eyebrow && (
                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-red mb-2">
                    {eyebrow}
                </div>
            )}
            <h2 className="font-brand text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-ink leading-tight">
                {title}<span className="text-brand-red">.</span>
            </h2>
            {lede && <p className="mt-2 text-sm sm:text-base text-brand-muted max-w-2xl">{lede}</p>}
        </div>
        {children}
    </section>
);

export const LeaderRow = ({ row, rank, isAnon }) => {
    const segments = Object.entries(row.breakdown).sort((a, b) => b[1] - a[1]);
    const segTotal = segments.reduce((s, [, v]) => s + v, 0) || row.total || 1;
    const topPlatform = segments[0] && segments[0][0];
    const isPodium = !isAnon && rank >= 1 && rank <= 3;
    const Wrapper = isAnon ? "div" : Link;
    const wrapperProps = isAnon ? {} : { to: `/m/${row.id}` };
    const clickWord = row.total === 1 ? "click" : "clicks";

    return (
        <Wrapper
            {...wrapperProps}
            className={`flex items-center gap-4 px-5 py-4 transition-colors ${
                isAnon ? "bg-brand-bg" : "hover:bg-brand-bg"
            }`}
        >
            <div
                className={`flex-shrink-0 w-10 text-right font-brand font-bold leading-none ${
                    isAnon
                        ? "text-brand-faint text-base"
                        : isPodium
                            ? "text-brand-red text-lg"
                            : "text-brand-faint text-base"
                }`}
            >
                {isAnon ? "—" : `#${rank}`}
            </div>
            <div className="flex-1 min-w-0">
                <div
                    className={`font-brand font-bold truncate ${
                        isAnon ? "text-brand-muted" : "text-brand-ink"
                    }`}
                >
                    {row.name}
                </div>
                {!isAnon && topPlatform && (
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-brand-muted">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${platformColor(topPlatform)}`} />
                        <span>Top on {platformLabel(topPlatform)}</span>
                    </div>
                )}
                {segments.length > 1 && (
                    <div className="mt-2 h-1 rounded-full overflow-hidden flex bg-brand-panel">
                        {segments.map(([platform, count]) => {
                            const segPct = Math.max(2, Math.round((count / segTotal) * 100));
                            return (
                                <div
                                    key={platform}
                                    className={`${platformColor(platform)} h-full`}
                                    style={{ width: `${segPct}%` }}
                                    title={`${platform}: ${count}`}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="text-right flex-shrink-0">
                {!isAnon && typeof row.totaltime === "number" ? (
                    <>
                        <div className="font-brand font-bold leading-none text-lg text-brand-ink">
                            {formatDuration(row.totaltime)}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-brand-faint mt-1">
                            {formatNumber(row.total)} {clickWord}
                        </div>
                    </>
                ) : (
                    <>
                        <div
                            className={`font-brand font-bold leading-none text-lg ${
                                isAnon ? "text-brand-muted" : "text-brand-ink"
                            }`}
                        >
                            {formatNumber(row.total)}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-brand-faint mt-1">
                            {clickWord}
                        </div>
                    </>
                )}
            </div>
        </Wrapper>
    );
};

export const GeoBar = ({ label, prefix, value, max, accent }) => {
    const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
    return (
        <div>
            <div className="flex items-baseline justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                    {prefix && <span className="text-base flex-shrink-0">{prefix}</span>}
                    <span className="truncate text-brand-ink font-medium">{label}</span>
                </div>
                <span className="font-brand font-bold text-brand-ink">{formatNumber(value)}</span>
            </div>
            <div className="mt-1.5 h-1.5 bg-brand-panel rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${accent}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

export const buildCountriesList = (rows) =>
    asRows(rows).map((r) => ({
        key: r.x,
        label: r.x || "Unknown",
        flag: countryFlag(r.x),
        value: r.y || 0,
    }));

export const buildCitiesList = (rows) =>
    asRows(rows).map((r) => ({
        key: `${r.x}-${r.country || ""}`,
        label: r.x || "Unknown",
        country: r.country,
        flag: countryFlag(r.country),
        value: r.y || 0,
    }));

export const GeoSection = ({ eyebrow, title, lede, countries, cities }) => {
    const maxCountry = Math.max(1, ...countries.map((r) => r.value));
    const maxCity = Math.max(1, ...cities.map((r) => r.value));
    return (
        <Section eyebrow={eyebrow} title={title} lede={lede}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
                <div className="rounded-2xl bg-white border border-brand-panel p-6 sm:p-7 shadow-brand">
                    <h3 className="font-brand text-lg sm:text-xl font-bold text-brand-ink mb-5">
                        Countries<span className="text-brand-red">.</span>
                    </h3>
                    {countries.length === 0 ? (
                        <div className="text-sm text-brand-muted">No data yet.</div>
                    ) : (
                        <div className="space-y-3.5">
                            {countries.slice(0, 10).map((row) => (
                                <GeoBar
                                    key={row.key}
                                    label={row.label}
                                    prefix={row.flag}
                                    value={row.value}
                                    max={maxCountry}
                                    accent="bg-brand-red"
                                />
                            ))}
                        </div>
                    )}
                </div>
                <div className="rounded-2xl bg-white border border-brand-panel p-6 sm:p-7 shadow-brand">
                    <h3 className="font-brand text-lg sm:text-xl font-bold text-brand-ink mb-5">
                        Cities<span className="text-brand-red">.</span>
                    </h3>
                    {cities.length === 0 ? (
                        <div className="text-sm text-brand-muted">No data yet.</div>
                    ) : (
                        <div className="space-y-3.5">
                            {cities.slice(0, 10).map((row) => (
                                <GeoBar
                                    key={row.key}
                                    label={row.country ? `${row.label}, ${row.country}` : row.label}
                                    prefix={row.flag}
                                    value={row.value}
                                    max={maxCity}
                                    accent="bg-brand-red-soft"
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Section>
    );
};

export const SourceRow = ({ row, max, isPlatform }) => {
    const pct = max > 0 ? Math.max(2, Math.round((row.value / max) * 100)) : 0;
    return (
        <div>
            <div className="flex items-baseline justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                    {isPlatform && (
                        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${platformColor(row.key)}`} />
                    )}
                    <span className="truncate text-brand-ink font-medium">{row.label}</span>
                </div>
                <div className="flex items-baseline gap-2 flex-shrink-0">
                    <span className="font-brand font-bold text-brand-ink">{formatNumber(row.value)}</span>
                    {row.sub && <span className="text-xs text-brand-muted">{row.sub}</span>}
                </div>
            </div>
            <div className="mt-1.5 h-1.5 bg-brand-panel rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full ${isPlatform ? platformColor(row.key) : "bg-brand-ink"}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
};
